import { useMemo } from 'react';
import { useTranscript } from './queries';
import { useTranscriptExcerpts } from './useTranscriptExcerpts';
import { useCodes } from '../codes/queries';
import { BOT_USER_ID } from 'constants/UserConstants';

// The one definition of "this excerpt carries a bot coding" — the
// comparison page's default-coder redirect and the AI coding modal must
// never disagree about it.
export const hasBotCoding = (excerpt) =>
  (excerpt?.codings || []).some((coding) => coding.user_id === BOT_USER_ID);

/**
 * Hook to get bot-coded excerpt IDs from a transcript
 * @param {string|number} transcriptId - The ID of the transcript
 * @param {string|number} projectId - The ID of the project
 * @returns {Object} Object containing bot excerpt IDs and codes availability
 */
export function useBotExcerpts(transcriptId, projectId) {
  const { isLoading: transcriptLoading, error: transcriptError } = useTranscript(transcriptId);
  // Resolved reactively through the detail caches (ids-only model), so a
  // bot coding added or removed re-derives the list without a refetch.
  const { excerpts } = useTranscriptExcerpts(transcriptId);
  const { data: codesData, isLoading: codesLoading, error: codesError } = useCodes(projectId);

  const botExcerptIds = useMemo(() => {
    return excerpts
      .filter(hasBotCoding)
      .sort((a, b) => a.start - b.start)
      .map(excerpt => excerpt.id);
  }, [excerpts]);

  const hasCodes = useMemo(() => {
    return !!(codesData?.codes && codesData.codes.length > 0);
  }, [codesData]);

  return {
    botExcerptIds,
    hasCodes,
    isLoading: transcriptLoading || codesLoading,
    error: transcriptError || codesError
  };
}